import * as React from 'react';
import axios, { CancelTokenSource } from 'axios';
import { observer } from 'mobx-react-lite';
import { Select, Tooltip, Form } from 'antd';
import { FormInstance } from 'antd/lib/form';
import { debounce } from 'lodash';
import { IotaMetadataInput } from '../../types';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { ExternalSelectOption, ExternalSelectOptionMapper } from '../../misc/ExternalSelectOptionMapper';
import { Subject } from 'rxjs';
import appClient from '../../../common/services/AppClient';
import { endpoints } from '../../../../config';

const parameters = {
    endpointInputId: 'endpointInputId',
    endpointPath: 'endpointPath',
    itemsPath: 'itemsPath',
    valueField: 'valueField',
    labelField: 'labelField'
} as const;

interface Props {
    form: FormInstance;
    input: IotaMetadataInput;
    initialValue: unknown;
    settingsValues?: Record<string, unknown>;
    formFieldsSubject: Subject<Record<string, unknown>>;
    disabled?: boolean;
}

const ApplicationExternalSelectInput: React.FC<Props> = ({
    input,
    initialValue,
    settingsValues,
    formFieldsSubject,
    disabled = false
}) => {
    const [endpoint, setEndpoint] = React.useState<string | undefined>();
    const [options, setOptions] = React.useState<ExternalSelectOption[]>([]);
    const [isLoading, setIsLoading] = React.useState(false);

    const endpointCancelTokenRef = React.useRef<CancelTokenSource | null>(null);

    const inputValues = React.useMemo(() => input.values ?? {}, [input.values]);

    const endpointInputId = inputValues[parameters.endpointInputId];
    const endpointPath = inputValues[parameters.endpointPath];
    const itemsPath = inputValues[parameters.itemsPath];
    const valueField = inputValues[parameters.valueField];
    const labelField = inputValues[parameters.labelField];

    const applyEndpointValue = React.useCallback(async (value: unknown) => {
        const rawEndpoint = typeof value === 'string' ? value.trim() : undefined;

        if (!rawEndpoint) {
            setEndpoint(undefined);
            return;
        }

        // If endpoint does not contain environment placeholder, use it as-is
        if (!rawEndpoint.includes('${ENV:')) {
            setEndpoint(rawEndpoint);
            return;
        }

        if (endpointCancelTokenRef.current) {
            endpointCancelTokenRef.current.cancel();
            endpointCancelTokenRef.current = null;
        }

        const source = axios.CancelToken.source();
        endpointCancelTokenRef.current = source;

        // Resolve endpoint from IOTA metadata
        try {
            const response = await appClient.get<string>(
                `${endpoints.iotaUrl}metadata/resolve-endpoint?endpoint=${encodeURIComponent(
                    rawEndpoint
                )}&isRequired=true`,
                'json',
                source.token
            );

            if (response.isErr()) {
                setEndpoint(undefined);
                return;
            }

            const resolvedEndpoint = typeof response.value === 'string' ? response.value.trim() : undefined;
            setEndpoint(resolvedEndpoint);
        } catch (error) {
            console.error('Error resolving endpoint from IOTA metadata: ', error);
            setEndpoint(undefined);
        } finally {
            if (endpointCancelTokenRef.current === source) {
                endpointCancelTokenRef.current = null;
            }
        }
    }, []);

    const debouncedApplyEndpointValue = React.useMemo(
        () => debounce((value: unknown) => applyEndpointValue(value), 500),
        [applyEndpointValue]
    );

    const url = React.useMemo(() => {
        if (!endpoint) {
            return undefined;
        }

        let composedUrl = endpoint;

        if (endpointPath && endpointPath.length > 0) {
            composedUrl = endpoint.endsWith('/') ? `${endpoint}${endpointPath}` : `${endpoint}/${endpointPath}`;
        }

        const trimmedUrl = composedUrl.trim();

        return trimmedUrl.length > 0 ? trimmedUrl : undefined;
    }, [endpoint, endpointPath]);

    React.useEffect(() => {
        const source = axios.CancelToken.source();

        const getOptions = async () => {
            try {
                const isValueFieldValid = typeof valueField === 'string' && valueField.length > 0;
                const isLabelFieldValid = typeof labelField === 'string' && labelField.length > 0;

                if (!isValueFieldValid || !isLabelFieldValid) {
                    setOptions([]);
                    return;
                }

                if (!url) {
                    setOptions([]);
                    return;
                }

                setIsLoading(true);

                // Use proxy endpoint to avoid CORS issues and security concerns with external HTTP endpoints
                const proxyUrl = `${endpoints.manageUrl}system/proxy-external-url`;
                const response = await appClient.post<unknown>(proxyUrl, { url }, source.token);

                if (response.isErr()) {
                    setOptions([]);
                    return;
                }

                setOptions(ExternalSelectOptionMapper.mapRemoteData(response.value, itemsPath, valueField, labelField));
            } catch (error) {
                console.error('Error fetching external select options: ', error);
                setOptions([]);
            } finally {
                setIsLoading(false);
            }
        };

        getOptions();

        return () => source.cancel();
    }, [url, itemsPath, valueField, labelField]);

    // Set the initial endpoint value from the settings
    React.useEffect(() => {
        if (endpointInputId && settingsValues) {
            const endpointSettingValue = settingsValues[endpointInputId];
            applyEndpointValue(endpointSettingValue);
        }
    }, [applyEndpointValue, endpointInputId, settingsValues]);

    // Subscribe to form fields changes and update endpoint value
    React.useEffect(() => {
        const subscription = formFieldsSubject.subscribe(changedValues => {
            if (endpointInputId && endpointInputId in changedValues) {
                debouncedApplyEndpointValue(changedValues[endpointInputId]);
            }
        });

        return () => {
            subscription.unsubscribe();
            debouncedApplyEndpointValue.cancel();
        };
    }, [debouncedApplyEndpointValue, endpointInputId, formFieldsSubject]);

    return (
        <Form.Item
            name={input.id}
            initialValue={initialValue}
            label={
                <span>
                    {input.name}&nbsp;
                    <Tooltip title={input.description}>
                        <QuestionCircleOutlined />
                    </Tooltip>
                </span>
            }
        >
            <Select
                options={options}
                loading={isLoading}
                disabled={disabled}
                showSearch
                filterOption={(inputText, option) =>
                    option?.title && option.title.toLowerCase().includes(inputText.toLowerCase())
                }
            />
        </Form.Item>
    );
};

export default observer(ApplicationExternalSelectInput);
